import { Stack } from "@mui/material";
import { useMemo } from "react";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { oids } from "@/configs/Settings";
import type { ClinicalInsightChildProps } from "src-next/modules/clinical-insights/types/analysisTypes";
import { useProtocolAnalysisData } from "src-next/modules/clinical-insights/hooks/useProtocolAnalysisData";
import { getInsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import { useInsightEmptyLabels } from "src-next/modules/clinical-insights/components/workspace/config/insightEmptyLabels";
import { InsightWorkspace } from "src-next/modules/clinical-insights/components/workspace/shell/InsightWorkspace";

const texts = getInsightAnalysisTexts("protocolos");

const STATUS_LABELS: Record<string, string> = {
  applied: "Aplicado",
};

export function ProtocolAnalysis({
  dateRange,
  practitionerId = "all",
  practitioner,
  accentColor,
  onPatientStatsChange,
  practitionerFilter: practitionerSelectSlot,
  onLoadPhaseChange,
  onSelectAllPractitioners,
  onPreviousPeriod,
  periodDisplayLabel,
  isActive = true,
  fetchWarm,
}: ClinicalInsightChildProps) {
  const { organizationId } = useOrganizationContext();
  const author = useMemo(
    () => (organizationId ? `${oids.cnpj}-${organizationId}` : ""),
    [organizationId]
  );
  const practitionerFilter = practitionerId || "all";

  const {
    protocols,
    rankingItems,
    patientRows,
    loadPhase,
    patientsProgress,
    hasData,
    retry,
  } = useProtocolAnalysisData({
    author,
    practitionerFilter,
    dateRange,
    onLoadPhaseChange,
    fetchEnabled: fetchWarm ?? isActive,
    isActive,
  });

  const filterResetKey = `${practitionerFilter}-${dateRange?.startDate ?? ""}-${dateRange?.endDate ?? ""}`;

  const { emptyPeriodLabel, emptyPractitionerLabel } = useInsightEmptyLabels(
    dateRange,
    practitionerFilter,
    practitioner,
    periodDisplayLabel
  );

  const totalApplications = protocols.reduce((sum, p) => sum + p.count, 0);

  const rankingInsight = useMemo(() => {
    if (rankingItems.length === 0) return texts.rankingHint;
    const sorted = [...rankingItems].sort((a, b) => b.value - a.value);
    const percentage =
      totalApplications > 0
        ? Math.round((sorted[0].value / totalApplications) * 100)
        : 0;
    return `'${sorted[0].label}' representa ${percentage}% dos protocolos aplicados`;
  }, [rankingItems, totalApplications]);

  return (
    <Stack width="100%" height="100%" spacing={0} sx={{ minHeight: 0 }}>
      <InsightWorkspace
        accentColor={accentColor ?? "#673ab7"}
        texts={texts}
        practitionerFilter={practitionerSelectSlot}
        filterResetKey={filterResetKey}
        loadPhase={loadPhase}
        patientsProgress={patientsProgress}
        hasData={hasData || protocols.length > 0}
        isEmpty={protocols.length === 0 && loadPhase === "done"}
        emptyPeriodLabel={emptyPeriodLabel}
        emptyPractitionerLabel={emptyPractitionerLabel}
        showClearPractitionerAction={practitionerFilter !== "all"}
        onClearPractitioner={onSelectAllPractitioners}
        onPreviousPeriod={onPreviousPeriod}
        onRetry={retry}
        rankingChart={{ insightText: rankingInsight, items: rankingItems }}
        patientRows={patientRows}
        statusLabelByKey={STATUS_LABELS}
        onPatientStatsChange={onPatientStatsChange}
      />
    </Stack>
  );
}
